import React, { ReactNode, useState } from 'react';
import {
  DndContext,
  useSensor,
  useSensors,
  MouseSensor,
  DragOverEvent,
  DragEndEvent,
  DragStartEvent,
  DragCancelEvent,
  pointerWithin,
  rectIntersection,
  Active,
} from '@dnd-kit/core';
import { DragOverlay } from '../DragOverlay/DragOverlay';

interface CrossContainerDnDProviderProps {
  children: ReactNode;
  onDragEnd?: (event: DragEndEvent) => void;
  onDragStart?: (event: DragStartEvent) => void;
  onDragOver?: (event: DragOverEvent) => void;
  onDragCancel?: (event: DragCancelEvent) => void;
  onDragStartData?: (active: Active) => any[];
}

export const CrossContainerDnDProvider = ({
  children,
  onDragEnd,
  onDragStart,
  onDragOver,
  onDragCancel,
  onDragStartData,
}: CrossContainerDnDProviderProps) => {
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 10 },
    }),
  );

  const [draggedItems, setDraggedItems] = useState<any[] | null>(null);

  const customCollisionDetection = (args: any) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length) {
      const nonDropzone = pointerCollisions.filter((c) => !c.id.toString().includes('dropzone'));
      return nonDropzone.length ? nonDropzone : pointerCollisions;
    }

    const rectCollisions = rectIntersection(args);
    if (rectCollisions.length) {
      const nonDropzone = rectCollisions.filter((c) => !c.id.toString().includes('dropzone'));
      return nonDropzone.length ? nonDropzone : rectCollisions;
    }

    return [];
  };

  const handleDragStart = (event: DragStartEvent) => {
    let items: any[] = [];
    if (onDragStartData) items = onDragStartData(event.active);
    if (!items || items.length === 0) items = [{ id: event.active.id }];
    setDraggedItems(items);
    onDragStart?.(event);
  };

  const handleDragOver = (event: DragOverEvent) => {
    onDragOver?.(event);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setDraggedItems(null);
    onDragEnd?.(event);
  };

  const handleDragCancel = (event: DragCancelEvent) => {
    setDraggedItems(null);
    onDragCancel?.(event);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      {children}
      <DragOverlay draggedItems={draggedItems} />
    </DndContext>
  );
};
